import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  Paper,
  Divider,
  Grid,
  Skeleton,
} from "@mui/material";
import PartProcessTable from "./PartProcessTable";
import type { partOtherProcess, partProcess } from "@/api/breakdownService";
import { useState, useEffect } from "react";
import { isValidDecimalInput } from "@/utils/inputParse";
import {
  useCalculatePartOtherProcess,
  useCalculatePartProcess,
} from "@/hooks/useBreakdownLogic";
import PartOtherProcessTable from "./PartOtherProcessTable";

interface PartProcessProps {
  isOtherProcess?: boolean;
  data?: partProcess | partOtherProcess;
  isEditingPartProcess: boolean;
  isAdminPct: boolean;
  isProfitPct: boolean;
  formData: partProcess | partOtherProcess | null;
  setFormData: React.Dispatch<
    React.SetStateAction<partProcess | partOtherProcess>
  >;
  setFlaggedReason: React.Dispatch<React.SetStateAction<Set<string>>>;
  isLoading?: boolean;
}

const PartProcessSkeleton = ({
  type,
  isOtherProcess,
}: {
  type: "OEM" | "Spare";
  isOtherProcess?: boolean;
}) => (
  <Paper
    elevation={0}
    sx={{ border: "1px solid #e0e0e0", borderRadius: 2, overflow: "hidden", mb: 2 }}
  >
    <Box
      sx={{
        bgcolor: type === "OEM" ? "#e3f2fd" : "#fff3e0",
        px: 3,
        py: 2,
        borderBottom: "1px solid #e0e0e0",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: type === "OEM" ? "#1976d2" : "#f57c00",
          fontSize: "1rem",
        }}
      >
        {type} Part{isOtherProcess ? " Other" : ""} Process
      </Typography>
    </Box>
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {(["Cost", "Admin", "Profit"] as const).map((label) => (
          <Grid key={label} size={{ xs: 12, md: 4 }}>
            <Skeleton variant="text" width={120} height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rounded" height={40} />
          </Grid>
        ))}
      </Grid>
      <Divider sx={{ my: 3 }} />
      {isOtherProcess ? (
        <PartOtherProcessTable
          title={`${type} Part Other Process`}
          rows={[]}
          setFormData={() => {}}
          isEditingPartProcess={false}
          type={type}
          setFlaggedReason={() => {}}
        />
      ) : (
        <PartProcessTable
          title={`${type} Part Process`}
          rows={[]}
          setFormData={() => {}}
          isEditingPartProcess={false}
          type={type}
          setFlaggedReason={() => {}}
        />
      )}
    </Box>
  </Paper>
);

const PartProcess = ({
  isOtherProcess,
  data,
  isEditingPartProcess,
  isAdminPct,
  isProfitPct,
  formData,
  setFormData,
  setFlaggedReason,
  isLoading,
}: PartProcessProps) => {
  const [adminOEMInput, setAdminOEMInput] = useState<string>((0).toFixed(2));
  const [adminSpareInput, setAdminSpareInput] = useState<string>((0).toFixed(2));
  const [profitOEMInput, setProfitOEMInput] = useState<string>((0).toFixed(2));
  const [profitSpareInput, setProfitSpareInput] = useState<string>((0).toFixed(2));

  useEffect(() => {
    if (isEditingPartProcess) return;
    if (data && "admin_oem_part_process" in data) {
      setFormData(data);
      setAdminOEMInput(Number(data.admin_oem_part_process ?? 0).toFixed(2));
      setAdminSpareInput(Number(data.admin_spare_part_process ?? 0).toFixed(2));
      setProfitOEMInput(Number(data.profit_oem_part_process ?? 0).toFixed(2));
      setProfitSpareInput(Number(data.profit_spare_part_process ?? 0).toFixed(2));
    } else if (data && "admin_oem_part_other_process" in data) {
      setFormData(data);
      setAdminOEMInput(Number(data.admin_oem_part_other_process ?? 0).toFixed(2));
      setAdminSpareInput(
        Number(data.admin_spare_part_other_process ?? 0).toFixed(2),
      );
      setProfitOEMInput(Number(data.oem_profit_part_other_process ?? 0).toFixed(2));
      setProfitSpareInput(
        Number(data.spare_profit_part_other_process ?? 0).toFixed(2),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isEditingPartProcess]);

  useEffect(() => {
    if (isOtherProcess) {
      setFormData(
        (prev) =>
          ({
            ...prev,
            admin_oem_part_other_process: parseFloat(adminOEMInput) || 0,
            admin_spare_part_other_process: parseFloat(adminSpareInput) || 0,
            oem_profit_part_other_process: parseFloat(profitOEMInput) || 0,
            spare_profit_part_other_process: parseFloat(profitSpareInput) || 0,
          }) as partOtherProcess,
      );
    } else {
      setFormData(
        (prev) =>
          ({
            ...prev,
            admin_oem_part_process: parseFloat(adminOEMInput) || 0,
            admin_spare_part_process: parseFloat(adminSpareInput) || 0,
            profit_oem_part_process: parseFloat(profitOEMInput) || 0,
            profit_spare_part_process: parseFloat(profitSpareInput) || 0,
          }) as partProcess,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adminOEMInput,
    adminSpareInput,
    profitOEMInput,
    profitSpareInput,
    isOtherProcess,
  ]);

  const {
    totalOEMPartProcessCost,
    totalSparePartProcessCost,
    totalOEMPartProcess,
    totalSparePartProcess,
  } = useCalculatePartProcess(formData as partProcess, isAdminPct, isProfitPct);

  const { totalOEMPartOtherProcessCost, totalSparePartOtherProcessCost } =
    useCalculatePartOtherProcess(formData as partOtherProcess);

  const renderProcessRow = (type: "OEM" | "Spare") => (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        overflow: "hidden",
        mb: 2,
      }}
    >
      <Box
        sx={{
          bgcolor: type === "OEM" ? "#e3f2fd" : "#fff3e0",
          px: 3,
          py: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: type === "OEM" ? "#1976d2" : "#f57c00",
            fontSize: "1rem",
          }}
        >
          {type} Part{isOtherProcess ? " Other" : ""} Process
        </Typography>
      </Box>

      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}
                >
                  {type} Part{isOtherProcess ? " Other" : ""} Process Cost
                </Typography>
                <TextField
                  disabled
                  value={Number(
                    type === "OEM"
                      ? isOtherProcess
                        ? totalOEMPartOtherProcessCost
                        : totalOEMPartProcessCost
                      : isOtherProcess
                        ? totalSparePartOtherProcessCost
                        : totalSparePartProcessCost ?? 0,
                  ).toFixed(2)}
                  size="small"
                  fullWidth
                  sx={{
                    "& input": { textAlign: "right" },
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">THB</InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}
                >
                  Admin {type} Part{isOtherProcess ? " Other" : ""} Process
                </Typography>
                <TextField
                  disabled={!isEditingPartProcess}
                  value={type === "OEM" ? adminOEMInput : adminSpareInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (isValidDecimalInput(val, 2)) {
                      if (type === "OEM") {
                        setAdminOEMInput(val.trim());
                        setFlaggedReason((prev) =>
                          new Set(prev).add(`Admin OEM Part${isOtherProcess ? " Other" : ""} Process`),
                        );
                      } else {
                        setAdminSpareInput(val.trim());
                        setFlaggedReason((prev) =>
                          new Set(prev).add(`Admin Spare Part${isOtherProcess ? " Other" : ""} Process`),
                        );
                      }
                    }
                  }}
                  onBlur={() => {
                    if (type === "OEM") {
                      setAdminOEMInput((parseFloat(adminOEMInput) || 0).toFixed(2));
                    } else {
                      setAdminSpareInput((parseFloat(adminSpareInput) || 0).toFixed(2));
                    }
                  }}
                  size="small"
                  fullWidth
                  sx={{
                    "& input": { textAlign: "right" },
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {isAdminPct ? "%" : "THB"}
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}
                >
                  Profit {type} Part{isOtherProcess ? " Other" : ""} Process
                </Typography>
                <TextField
                  disabled={!isEditingPartProcess}
                  value={type === "OEM" ? profitOEMInput : profitSpareInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (isValidDecimalInput(val, 2)) {
                      if (type === "OEM") {
                        setProfitOEMInput(val.trim());
                        setFlaggedReason((prev) =>
                          new Set(prev).add(`Profit OEM Part${isOtherProcess ? " Other" : ""} Process`),
                        );
                      } else {
                        setProfitSpareInput(val.trim());
                        setFlaggedReason((prev) =>
                          new Set(prev).add(`Profit Spare Part${isOtherProcess ? " Other" : ""} Process`),
                        );
                      }
                    }
                  }}
                  onBlur={() => {
                    if (type === "OEM") {
                      setProfitOEMInput((parseFloat(profitOEMInput) || 0).toFixed(2));
                    } else {
                      setProfitSpareInput((parseFloat(profitSpareInput) || 0).toFixed(2));
                    }
                  }}
                  size="small"
                  fullWidth
                  sx={{
                    "& input": { textAlign: "right" },
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {isProfitPct ? "%" : "THB"}
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />
        {isOtherProcess ? (
          <PartOtherProcessTable
            title={`${type} Part Other Process`}
            rows={
              type === "OEM"
                ? (formData as partOtherProcess)?.oem_other_process
                : (formData as partOtherProcess)?.spare_other_process
            }
            setFormData={
              setFormData as React.Dispatch<
                React.SetStateAction<partOtherProcess>
              >
            }
            isEditingPartProcess={isEditingPartProcess}
            type={type}
            setFlaggedReason={setFlaggedReason}
          />
        ) : (
          <PartProcessTable
            title={`${type} Part${isOtherProcess ? " Other" : ""} Process`}
            rows={
              type === "OEM"
                ? (formData as partProcess)?.oem_part_process
                : (formData as partProcess)?.spare_part_process
            }
            setFormData={
              setFormData as React.Dispatch<React.SetStateAction<partProcess>>
            }
            isEditingPartProcess={isEditingPartProcess}
            type={type}
            rowsTotal={
              type === "OEM" ? totalOEMPartProcess : totalSparePartProcess
            }
            setFlaggedReason={setFlaggedReason}
          />
        )}
      </Box>
    </Paper>
  );

  if (isLoading) {
    return (
      <Box sx={{ mt: 2, maxHeight: "70vh", overflow: "auto", pr: 1 }}>
        <PartProcessSkeleton type="OEM" isOtherProcess={isOtherProcess} />
        <PartProcessSkeleton type="Spare" isOtherProcess={isOtherProcess} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 2,
        maxHeight: "70vh",
        overflow: "auto",
        pr: 1,
      }}
    >
      {renderProcessRow("OEM")}
      {renderProcessRow("Spare")}
    </Box>
  );
};

export default PartProcess;
